import useRouterString, {
  getRouterString,
} from "@raiden/library/hooks/router/useRouterString";
import { useApi } from "@raiden/library/hooks/useApi";
import { serverApiFetcher } from "@raiden/library/hooks/useApiFetcher";
import useInterpolateTranslatedField from "@raiden/library/hooks/useInterpolateTranslatedField";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import generatePampaUri from "@raiden/library/libraries/utils/generatePampaUri";
import Seo from "../../../components/Seo";
import ArticlesView from "../../../containers/Articles/View";
import { getIdFromSlug } from "../../../helpers/getIdFromSlug";

/**
 * @param {object} params
 * @param {number} params.postId
 * @returns {string}
 */
function getPostUrl({ postId }) {
  return generateApiUri({
    id: "@posts.show",
    parameters: { postId },
    query: {
      fields: [
        "post.current_revision",
        "post.categories",
        "post.revision.cover",
      ],
    },
  });
}

/**
 * @typedef {object} PageProps
 * @property {String} layout
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>} [postResponse]
 */
/**
 * @param {PageProps} props
 */
function Page({ postResponse }) {
  const translate = useInterpolateTranslatedField();

  const articleSlug = useRouterString("articleSlug");

  const postId = getIdFromSlug(articleSlug);

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Post").Post>} */
  const { swrResponse: swrResponsePost } = useApi(
    postId !== undefined
      ? getPostUrl({
          postId,
        })
      : null,
    {
      swrConfig: {
        fallbackData: postResponse,
      },
    },
  );

  const post = swrResponsePost.data?.data;
  const currentRevision = post?.current_revision;
  const title = translate(currentRevision?.seo_title ?? currentRevision?.title);
  const description = translate(
    currentRevision?.seo_description ?? currentRevision?.summary,
  );
  const cover = currentRevision?.cover;
  const imageUrl = cover
    ? generatePampaUri({
        image: cover,
        mode: "crop",
        format: "default",
      })
    : undefined;

  return (
    <>
      <Seo
        title={title}
        description={description}
        ogType="article"
        imageUrl={imageUrl}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: title,
          image: imageUrl ? [imageUrl] : [],
          datePublished: post?.published_at,
          dateModified: post?.updated_at,
        }}
      />

      <ArticlesView swrResponsePost={swrResponsePost} />
    </>
  );
}

export default Page;

/**
 * @param {import("next").NextPageContext} context
 *
 * @returns {Promise<PageProps>}
 */
Page.getInitialProps = async function (context) {
  /** @type {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post> | undefined} */
  let postResponse = undefined;

  if (typeof window === "undefined") {
    const postId = getIdFromSlug(getRouterString(context.query.articleSlug));

    postResponse =
      postId !== undefined
        ? await serverApiFetcher(getPostUrl({ postId })).catch(() => {})
        : undefined;
  }

  return {
    layout: "public",
    postResponse,
  };
};
